/**
 * Contratos e regras das OFERTAS na praça.
 *
 * A oferta é uma carga que o produtor coloca na praça: **anônima**, com
 * **volume estimado** e **% de primeira**, e o comprador responde **interesse**.
 *
 * ═══ A OFERTA NÃO TEM PREÇO (059) ═══════════════════════════════════════════
 *
 * A chamada de hoje é para uma carga do dia 15, e o preço é o do DIA DA CARGA.
 * O ciclo anterior congelava a cotação na publicação (`cotacao_snapshot`) e
 * criava a venda com aquele valor — o que prometia um preço que a praça pode
 * mudar antes do carregamento. A cotação vigente entra na chamada como
 * REFERÊNCIA, com data e etiqueta; nada é congelado como preço combinado, e a
 * venda nasce sem preço e sem kg (os dois entram na emissão).
 *
 * Daí sai o resto: sem kg no pedido, sem fechamento parcial, sem saldo. Fechar
 * com um comprador fecha a carga — quem quiser dividir publica duas ofertas,
 * que é o que faria no WhatsApp de qualquer forma.
 *
 * ⚠️ O DTO ANÔNIMO deste arquivo é o mais sensível da plataforma: quem o lê é
 * qualquer um com o link, e o que ele NÃO pode conter (org, nome, fazenda,
 * telefone do produtor) é vigiado por teste de contrato sobre as chaves.
 */

/**
 * ═══ O INTERRUPTOR (13/09/2026) ═════════════════════════════════════════════
 *
 * **Ofertas está DESLIGADA.** Decisão do Hugo, olhando a tela Praça em produção:
 * a feature não está pronta para ficar de pé. O que ele viu, e é tudo verdade:
 *
 *   • a data do preço vinha rotulada "Hoje" mesmo sendo de dois dias antes
 *     (`Hoje,` estava cravado em `bloco-oferta.tsx`, antes da data da cotação);
 *   • "Quem está ofertando" repetia o mesmo nome em duas linhas (`nome` e
 *     `fazenda` da mesma org) e não tinha telefone — sendo que a razão do bloco
 *     existir é o comprador ligar;
 *   • e o card terminava em beco: "você disse que tem interesse · aguardando o
 *     produtor", sem ação, sem prazo e sem saber se ele viu.
 *
 * ⚠️ DESLIGAR, NÃO APAGAR. As ofertas publicadas, as respostas e os tokens
 * continuam no banco, intactos — nenhuma migração, nenhum DELETE. Voltar é
 * trocar este `false` por `true`; é por isso que ele é uma constante e não uma
 * dúzia de telas comentadas.
 *
 * ⚠️ QUEM LÊ ISTO É OS DOIS LADOS. O menu some E a API recusa — a mesma regra
 * dos módulos (CLAUDE.md, multi-tenant): tela escondida com rota viva é uma
 * porta aberta, não uma feature desligada.
 *
 * O link público `/o/:token` já enviado no WhatsApp responde 404, e a página
 * dele — que já tratava token inexistente — diz "esta oferta não está mais
 * disponível. Peça um link novo para quem mandou a chamada." É a frase certa: o
 * comprador não precisa saber que a feature saiu do ar, precisa saber o que
 * fazer.
 */
// `: boolean` de propósito. Sem a anotação o TypeScript estreita para o literal
// `false`, marca o outro lado de todo `if` como código morto, e o dia de religar
// vira uma sequência de erros de tipo em arquivos que ninguém tocou.
export const OFERTAS_ATIVAS: boolean = false;

export type VariedadeOferta = "PRATA" | "NANICA";

export const VARIEDADES_OFERTA: VariedadeOferta[] = ["PRATA", "NANICA"];

export function ehVariedadeOferta(v: unknown): v is VariedadeOferta {
  return v === "PRATA" || v === "NANICA";
}

/** % de primeira: um número entre 0 e 100. Nada de três faixas somando 100. */
export function pctPrimeiraValida(pct: unknown): pct is number {
  return typeof pct === "number" && isFinite(pct) && pct >= 0 && pct <= 100;
}

// ─── Faixa de qualidade (atalho de entrada, NUNCA um campo novo) ─────────────

/**
 * "Como está a banana": Boa · Média · Mais fraca.
 *
 * ⚠️ ISTO NÃO É UM CAMPO. A oferta continua carregando UM `pct_primeira`, e é
 * ele que vai para o banco, para a chamada ("85% de 1a") e para o DTO público.
 * A faixa é só um jeito de digitar o número com o polegar, no sol, sem teclado
 * numérico — para quem oferta carga e usa pouco celular. Virar enum, coluna ou
 * chave de DTO seria criar uma segunda verdade sobre a mesma coisa.
 *
 * ⚠️ A DERIVAÇÃO É DE MÃO ÚNICA: a faixa preenche o número, e o número ACENDE a
 * faixa. Digitar 74 acende "Média" e continua 74 — arredondar para 60 apagaria
 * o que o produtor sabe e a chamada diria outra coisa.
 */
export type FaixaQualidade = "BOA" | "MEDIA" | "FRACA";

export const FAIXAS_QUALIDADE: FaixaQualidade[] = ["BOA", "MEDIA", "FRACA"];

/** O valor que cada faixa ESCREVE no campo quando é tocada. */
export const PCT_DA_FAIXA: Record<FaixaQualidade, number> = {
  BOA: 85,
  MEDIA: 60,
  FRACA: 45,
};

/**
 * A faixa que CONTÉM um número — a que acende no formulário.
 *
 * As bandas são `>= 80` Boa, `50–79` Média, `< 50` Mais fraca. `null` sem
 * número: ao abrir a tela nenhuma faixa vem marcada, porque pré-selecionar
 * "Boa" faria a oferta sair com um número que ninguém informou.
 */
export function faixaDoPct(pct: number | null | undefined): FaixaQualidade | null {
  if (pct == null || !isFinite(pct)) return null;
  if (pct >= 80) return "BOA";
  if (pct >= 50) return "MEDIA";
  return "FRACA";
}

/**
 * Dias civis entre duas datas ISO (`ate - de`). Negativo quando `ate` é antes.
 *
 * `Date.UTC` sobre a string fatiada, nunca `new Date(iso)`: o servidor roda em
 * UTC e o Mac do Hugo em UTC-3, e a diferença em hora local erra por um dia
 * exatamente na virada — que é justamente onde "vencida há 1 dia" importa.
 */
export function diasEntreDatas(de: string, ate: string): number {
  const [a1, m1, d1] = de.split("-").map(Number);
  const [a2, m2, d2] = ate.split("-").map(Number);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86400000);
}

// ─── Status (pura) ───────────────────────────────────────────────────────────

/**
 * O estado da oferta, DERIVADO. Uma função só, usada na leitura e na escrita.
 *
 * A ordem das regras é a regra:
 *  1. RASCUNHO continua RASCUNHO — nada de virar ENCERRADA sozinho antes de ir
 *     para a praça (o produtor pode ter deixado o rascunho para a semana que vem).
 *  2. Fechou com alguém → FECHADA. Sem kg não há carga pela metade.
 *  3. Encerrada à mão continua encerrada.
 *  4. `disponivelEm` no passado → ENCERRADA (sem cron: quem detecta é a leitura,
 *     e ela grava `encerrada_em` na primeira vez que passar por aqui).
 */
export function statusDaOferta(entrada: {
  status: string;
  /** Já existe resposta FECHADO nesta oferta? */
  fechada: boolean;
  /** ISO "AAAA-MM-DD". */
  disponivelEm: string;
  encerradaEm: string | null;
  /** ISO "AAAA-MM-DD" — a data civil da fazenda, nunca `new Date()` aqui dentro. */
  hoje: string;
}): "RASCUNHO" | "NA_PRACA" | "FECHADA" | "ENCERRADA" {
  if (entrada.status === "RASCUNHO") return "RASCUNHO";
  if (entrada.fechada) return "FECHADA";
  if (entrada.encerradaEm) return "ENCERRADA";
  if (entrada.disponivelEm < entrada.hoje) return "ENCERRADA";
  return "NA_PRACA";
}

/** A oferta ainda aceita interesse? Só NA_PRACA — não há carga pela metade. */
export function aceitaResposta(status: string): boolean {
  return status === "NA_PRACA";
}

/**
 * 'OF-0001' — o próximo identificador da org, a partir dos que já existem.
 *
 * Pura e sobre a LISTA (não `MAX(...)` no SQL) para caber num teste: o formato é
 * contrato com o produtor (ele lê "OF-0001" no WhatsApp e na observação da
 * venda), e o zero à esquerda é o que mantém a ordem alfabética igual à
 * numérica. Concorrência é resolvida por fora, pelo UNIQUE (org, identificador)
 * + retry — ver `criarOferta`.
 */
export function proximoIdentificador(existentes: string[]): string {
  let maior = 0;
  for (const id of existentes) {
    const m = /^OF-(\d+)$/.exec(String(id ?? "").trim());
    if (!m) continue;
    const n = parseInt(m[1], 10);
    if (n > maior) maior = n;
  }
  const proximo = String(maior + 1);
  return `OF-${proximo.length >= 4 ? proximo : "0000".substring(proximo.length) + proximo}`;
}

// ─── A cotação como REFERÊNCIA (nunca como preço da oferta) ──────────────────

export interface ItemCotacaoDTO {
  qualidade: string | null;
  precoKg: number;
}

/**
 * A cotação vigente que acompanha a oferta. `referencial` vem junto e é
 * obrigatório: um preço sem a etiqueta do que ele é não diz nada ao comprador
 * — e é justamente a confusão que este ciclo existe para desfazer.
 */
export interface CotacaoDaOfertaDTO {
  /** ISO "AAAA-MM-DD" da publicação. */
  data: string;
  referencial: string;
  itens: ItemCotacaoDTO[];
}

// ─── DTO ANÔNIMO — o contrato do link público ────────────────────────────────

/**
 * O que QUALQUER UM com o link vê.
 *
 * ⚠️ NADA daqui identifica o produtor: nem org, nem nome, nem fazenda, nem
 * telefone, nem id de venda. A oferta é anônima até o produtor abrir os
 * detalhes para um comprador específico — e aí o nome sai por OUTRA rota
 * (`/detalhes?telefone=`), nunca por este DTO.
 *
 * `CHAVES_OFERTA_PUBLICA` é o teste de contrato: chave nova sem passar por aqui
 * reprova, que é exatamente o objetivo.
 */
export interface OfertaPublicaDTO {
  identificador: string;
  praca: { nome: string };
  variedade: string;
  /** Estimado. A página escreve "~8.000 kg" — ver o comentário do schema. */
  volumeKg: number;
  pctPrimeira: number;
  disponivelEm: string;
  /** A cotação de HOJE, como referência. `null` quando a praça não publicou. */
  cotacao: CotacaoDaOfertaDTO | null;
  status: string;
  /** O que ESTE telefone já fez. Nunca a resposta dos outros. */
  respondida: RespostaMinhaDTO | null;
}

/**
 * A resposta é INTERESSE, e só. Sem kg, sem "quero tudo" — quanto e quando se
 * acerta entre os dois, no WhatsApp.
 */
export interface RespostaMinhaDTO {
  detalhesAbertos: boolean;
}

export const CHAVES_OFERTA_PUBLICA = [
  "identificador",
  "praca",
  "variedade",
  "volumeKg",
  "pctPrimeira",
  "disponivelEm",
  "cotacao",
  "status",
  "respondida",
] as const;

/**
 * O que NUNCA pode aparecer no DTO anônimo — varrido recursivamente pelo teste.
 *
 * `produtor` está na lista de propósito: o bloco do produtor existe, mas sai por
 * `/detalhes?telefone=` e pela tela Praça (onde o servidor já checou o direito),
 * nunca no DTO que o link entrega a quem quer que seja.
 */
export const CHAVES_PROIBIDAS_OFERTA_PUBLICA = [
  "produtor",
  "produtorNome",
  "fazenda",
  "orgId",
  "org_id",
  "orgProdutorId",
  "telefone",
  "telefoneWhatsapp",
  "vendaId",
  "compradorId",
  "criadoPorUserId",
  "respostas",
  "custo",
] as const;

/** O bloco do produtor — só depois de ele abrir os detalhes. */
export interface ProdutorDaOfertaDTO {
  nome: string;
  fazenda: string;
  telefone: string;
}

// ─── DTOs do PRODUTOR ────────────────────────────────────────────────────────

export type TipoDeQuem = "VINCULADO" | "NO_RIZOMA" | "NOVO";

export interface QuemRespondeuDTO {
  /** Só quando `comprador_id` ou `org_compradora_id` resolveram; senão `null`. */
  nome: string | null;
  /** "(74) 9****-8888" — nunca o número inteiro na lista. */
  telefoneMascarado: string;
  tipo: TipoDeQuem;
  perfil: "CORRETOR" | "DISTRIBUIDOR" | null;
  /** Quantos produtores este comprador já acompanha no Rizoma. 0 = novo. */
  produtoresNoRizoma: number;
  cidade: string | null;
}

export interface RespostaOfertaDTO {
  id: string;
  quem: QuemRespondeuDTO;
  /** Instante ISO, ou `null` em quem só abriu. */
  respondidoEm: string | null;
  abertoEm: string | null;
  status: string;
  vendaId: string | null;
  /** "042/2026" quando a venda já foi emitida; `null` no rascunho. */
  vendaNumero: string | null;
}

export interface OfertaProdutorDTO {
  id: string;
  identificador: string;
  praca: string;
  pracaNome: string;
  variedade: string;
  volumeKg: number;
  pctPrimeira: number;
  disponivelEm: string;
  /** A cotação VIGENTE da praça, como referência. Nunca preço da oferta. */
  cotacao: CotacaoDaOfertaDTO | null;
  alcance: { compradores: number; abriram: number };
  status: string;
  publicadaEm: string | null;
  criadoEm: string;
  respostas: RespostaOfertaDTO[];
}

/** O que o formulário "Ofertar carga" precisa saber antes de existir oferta. */
export interface ContextoOfertaDTO {
  variedade: string;
  /** kg colhido e ainda não vendido nos últimos 7 dias; `null` sem base segura. */
  colhidoNaoVendido7d: number | null;
  /** % média de PRIMEIRA nas vendas emitidas do mês; `null` sem base. */
  pctPrimeiraDoMes: number | null;
  /** A cotação do dia da praça da org — referência, com etiqueta e validade. */
  cotacaoHoje: {
    data: string;
    referencial: string;
    validaAte: string | null;
    /** `valida_ate < hoje` — o quadro mostra o preço mesmo assim. */
    vencida: boolean;
    diasVencida: number;
    itens: ItemCotacaoDTO[];
  } | null;
  alcance: {
    /**
     * `slug`/`nome` são `null` quando não há praça ativa no cadastro — não
     * existe slug de fallback (ver `pracaDaOrg`). O formulário mostra "—".
     */
    praca: { slug: string | null; nome: string | null; compradoresNoRizoma: number };
    meusCompradores: number;
  };
}

/**
 * Um destinatário do painel de envio — uma linha da lista.
 *
 * ⚠️ `url` é `null` quando não há telefone, e é isso que troca o botão
 * "Enviar" por "Adicionar telefone". A linha NÃO some: o comprador existe, o
 * que falta é o número — e some-lo esconderia justamente o cadastro a
 * completar.
 */
export interface DestinatarioOfertaDTO {
  /** `cadastro:<id>` ou `praca:<orgId>` — chave estável da lista. */
  id: string;
  nome: string;
  /** "(74) 9****-8888", ou `null` sem telefone. Nunca o número inteiro. */
  telefoneMascarado: string | null;
  /** O `wa.me` pronto, com o texto já dentro. `null` sem telefone. */
  url: string | null;
  /** Do cadastro do produtor, ou da praça da oferta. */
  origem: "CADASTRO" | "PRACA";
  /** Só em `CADASTRO`: o id para abrir "Adicionar telefone". */
  compradorId: string | null;
}

/** A chamada montada no servidor + o painel de envio. */
export interface MensagemOfertaDTO {
  /** O texto ANÔNIMO — o do grupo, e o do "outro número". */
  texto: string;
  /** A URL pública, para o botão "Copiar link". */
  url: string;
  /**
   * Quem a chamada alcança, um por linha. Vazio = praça sem ninguém no Rizoma
   * e cadastro vazio; aí o painel mostra só o botão de copiar, que é o
   * mecanismo real de "toda a praça".
   */
  destinatarios: DestinatarioOfertaDTO[];
}

// ─── DTO da tela Praça (comprador logado) ────────────────────────────────────

export interface OfertaDaPracaDTO extends OfertaPublicaDTO {
  /** O id serve às ações do comprador — a oferta é anônima, o id não a identifica. */
  id: string;
  praca: { nome: string; slug: string };
  /**
   * O produtor, quando o comprador tem direito de vê-lo: detalhes abertos para
   * ele, ou VÍNCULO ATIVO. Derivado, sem flag por oferta — ver a rota da tela
   * Praça. `null` para a praça aberta, que é de quem a anonimidade protege.
   */
  produtor: ProdutorDaOfertaDTO | null;
}

export interface PracaCompradorDTO {
  /** As praças da org (ou as derivadas do vínculo, quando ela não escolheu). */
  pracas: Array<{ slug: string; nome: string }>;
  /** true quando as praças vieram do VÍNCULO — a tela pede para escolher. */
  derivadaDoVinculo: boolean;
  ofertas: OfertaDaPracaDTO[];
}

/** As chaves do DTO da tela Praça — o mesmo contrato, com o que o login permite. */
export const CHAVES_OFERTA_PRACA: readonly string[] =
  (CHAVES_OFERTA_PUBLICA as readonly string[]).concat(["id", "produtor"]);

// ─── Home do produtor ────────────────────────────────────────────────────────

/** Uma oferta como o card "Ofertas em andamento" da home a lê. */
export interface ResumoOfertaHome {
  id: string;
  identificador: string;
  variedade: string;
  volumeKg: number;
  pctPrimeira: number;
  disponivelEm: string;
  status: string;
  /** Quantos compradores disseram que têm interesse (dispensados fora). */
  interessados: number;
  publicadaEm: string | null;
}

/**
 * "3 compradores querem sua Prata".
 *
 * Fora de `PendenciaDTO` de propósito: aquele DTO é sobre ROMANEIO (tem venda,
 * número, comprador, vencimento), e encaixar oferta ali obrigaria a inventar um
 * `vendaId` que não existe — o tipo de mentira que depois vira `if` espalhado.
 */
export interface PendenciaOfertaDTO {
  ofertaId: string;
  identificador: string;
  variedade: string;
  interessados: number;
  volumeKg: number;
}
